import styled from '@emotion/styled';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';

import Button from '@/components/common/Button';

import type { NoticesItem } from '@/types/notices.types';
import { getPlatformDisplayLabel } from '@/utils/convertStringFormat';
import { Theme } from '@/styles/Theme';

interface Props {
  notice: NoticesItem;
  onEdit: () => void;
  onDelete: () => void;
  isExpanded?: boolean;
  onToggle?: () => void;
}

const NoticeListItem = ({
  notice,
  onEdit,
  onDelete,
  isExpanded = false,
  onToggle,
}: Props) => {
  return (
    <>
      <tr
        onClick={onToggle}
        style={onToggle ? { cursor: 'pointer' } : undefined}
      >
        <Td>
          <TitleCell>
            {notice.important === 1 && <ImportantDot />}
            {notice.is_landing_popup === 1 && <LandingBadge>랜딩</LandingBadge>}
            <TitleText>{notice.title}</TitleText>
            {onToggle &&
              (isExpanded ? (
                <ExpandIcon as={IoIosArrowUp} />
              ) : (
                <ExpandIcon as={IoIosArrowDown} />
              ))}
          </TitleCell>
        </Td>
      <Td>{getPlatformDisplayLabel(notice.platform)}</Td>
      <Td>{notice.important === 1 ? 'O' : '-'}</Td>
      <Td>{notice.is_active === 1 ? 'O' : '-'}</Td>
      <Td>{notice.updatedAt ?? '-'}</Td>
      <Td onClick={(e) => e.stopPropagation()}>
        <ActionCell>
          <Button
            backgroundColor={Theme.colors.G_5}
            color={Theme.colors.BLACK}
            width='4.375rem'
            height='2rem'
            fontSize='0.8125rem'
            onClick={onEdit}
          >
            수정
          </Button>
          <Button
            backgroundColor={Theme.colors.WARNING_RED}
            width='4.375rem'
            height='2rem'
            fontSize='0.8125rem'
            onClick={onDelete}
          >
            삭제
          </Button>
        </ActionCell>
      </Td>
    </tr>
    {isExpanded && onToggle && (
      <tr>
        <ExpandedTd colSpan={6}>
          <ExpandedContent>
            <ExpandedTitle>{notice.title}</ExpandedTitle>
            <ExpandedBody>{notice.content}</ExpandedBody>
            {notice.image && notice.image.length > 0 && (
              <ImgContainer>
                {notice.image.map((img, idx) => (
                  <StyledImg
                    loading="lazy"
                    key={idx}
                    src={img}
                    alt={`notice_img_${idx}`}
                  />
                ))}
              </ImgContainer>
            )}
          </ExpandedContent>
        </ExpandedTd>
      </tr>
    )}
    </>
  );
};

export default NoticeListItem;

const Td = styled.td`
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  border-bottom: 0.0625rem solid ${Theme.colors.G_5};
  vertical-align: middle;
`;

const TitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 25rem;
  min-width: 0;
`;

const TitleText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExpandIcon = styled.div`
  flex-shrink: 0;
  font-size: 1rem;
  color: ${Theme.colors.G_2};
`;

const ImportantDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${Theme.colors.B_3};
  flex-shrink: 0;
`;

const LandingBadge = styled.span`
  padding: 0.125rem 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${Theme.colors.WHITE};
  background-color: ${Theme.colors.B_3};
  border-radius: 0.25rem;
  flex-shrink: 0;
`;

const ActionCell = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ExpandedTd = styled.td`
  padding: 0;
  background-color: ${Theme.colors.G_7};
  border-bottom: 0.0625rem solid ${Theme.colors.G_5};
  vertical-align: top;
`;

const ExpandedContent = styled.div`
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
`;

const ExpandedTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ExpandedBody = styled.div`
  white-space: pre-line;
  color: ${Theme.colors.G_1};
  margin-bottom: 1rem;
`;

const ImgContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StyledImg = styled.img`
  max-width: 100%;
  max-height: 12.5rem;
  border-radius: 0.25rem;
`;
